import { tourCustomFieldsRoute } from '@/features/dashboard/tourCustomFields/routes/tourCustomFieldsRoute.ts';
import { useNavigate } from '@tanstack/react-router';
import TourConfigBreadcrumbs from '@/features/dashboard/tourConfig/components/TourConfigBreadcrumbs.tsx';
import TourConfigHeader from '@/features/dashboard/tourConfig/components/TourConfigHeader.tsx';
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.tsx';
import { ListPlus, Plus } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import AddCustomFieldModal from '@/features/dashboard/tourCustomFields/components/AddCustomFieldModal.tsx';
import TextCustomFieldAddModal from '@/features/dashboard/tourCustomFields/components/TextCustomFieldAddModal.tsx';
import CustomFieldsSummary from '@/features/dashboard/tourCustomFields/components/CustomFieldsSummary.tsx';
import useGetTourCustomFields from '@/features/dashboard/tourCustomFields/hooks/useGetTourCustomFields.ts';
import TextCustomFieldEditModal from '@/features/dashboard/tourCustomFields/components/TextCustomFieldEditModal.tsx';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import DropdownCustomFieldAddModal from '@/features/dashboard/tourCustomFields/components/DropdownCustomFieldAddModal.tsx';
import DropdownCustomFieldEditModal from '@/features/dashboard/tourCustomFields/components/DropdownCustomFieldEditModal.tsx';
import LongTextCustomFieldAddModal from '@/features/dashboard/tourCustomFields/components/LongTextCustomFieldAddModal.tsx';
import LongTextCustomFieldEditModal from '@/features/dashboard/tourCustomFields/components/LongTextCustomFieldEditModal.tsx';
import CheckboxCustomFieldAddModal from '@/features/dashboard/tourCustomFields/components/CheckboxCustomFieldAddModal.tsx';
import CheckboxCustomFieldEditModal from '@/features/dashboard/tourCustomFields/components/CheckboxCustomFieldEditModal.tsx';
import CustomFieldDefinitionDto from '@/features/dashboard/tourCustomFields/types/_CustomFieldDefinitionDto.ts';
import { DropdownCustomFieldDefinitionDto } from '@/features/dashboard/tourCustomFields/types/DropdownCustomFieldDefinitionDto.ts';
import { TextCustomFieldDefinitionDto } from '@/features/dashboard/tourCustomFields/types/TextCustomFieldDefinitionDto.ts';
import { LongTextCustomFieldDefinitionDto } from '@/features/dashboard/tourCustomFields/types/LongTextCustomFieldDefinitionDto.ts';
import { CheckboxCustomFieldDefinitionDto } from '@/features/dashboard/tourCustomFields/types/CheckboxCustomFieldDefinitionDto.ts';

const TourCustomFieldsConfigPage = () => {
  const navigate = useNavigate();
  const { tourId } = tourCustomFieldsRoute.useParams();

  const onSubmit = async () => {
    navigate({
      to: `/app/tours/${tourId}/config/consents`,
    });
  };

  const [addCustomFieldModalOpen, setAddCustomFieldModalOpen] = useState(false);

  const handleNewCustomField = () => {
    setAddCustomFieldModalOpen(true);
  };

  const [activeModal, setActiveModal] = useState<FieldType | null>(null);
  const [activeFieldDef, setActiveFieldDef] =
    useState<CustomFieldDefinitionDto | null>(null);

  const { data: customFields, isLoading } = useGetTourCustomFields(tourId);

  const openEditModal = (fieldDef: CustomFieldDefinitionDto) => {
    setActiveModal(fieldDef.type);
    setActiveFieldDef(fieldDef);
  };

  const onModalClose = () => {
    setActiveModal(null);
    setActiveFieldDef(null);
  };

  return (
    <div className="space-y-4">
      <TourConfigBreadcrumbs />
      <div className="flex-1 space-y-4">
        <TourConfigHeader
          title="Formularz rejestracyjny"
          isLoading={false}
          submit={() => onSubmit()}
        />

        {addCustomFieldModalOpen && (
          <AddCustomFieldModal
            onClose={() => setAddCustomFieldModalOpen(false)}
            setActiveModal={setActiveModal}
          />
        )}

        {activeModal === FieldType.TEXT &&
          (activeFieldDef === null ? (
            <TextCustomFieldAddModal onClose={() => setActiveModal(null)} />
          ) : (
            <TextCustomFieldEditModal
              fieldDef={activeFieldDef as TextCustomFieldDefinitionDto}
              onClose={onModalClose}
            />
          ))}

        {activeModal === FieldType.LONG_TEXT &&
          (activeFieldDef === null ? (
            <LongTextCustomFieldAddModal onClose={() => setActiveModal(null)} />
          ) : (
            <LongTextCustomFieldEditModal
              fieldDef={activeFieldDef as LongTextCustomFieldDefinitionDto}
              onClose={onModalClose}
            />
          ))}

        {activeModal === FieldType.CHECKBOX &&
          (activeFieldDef === null ? (
            <CheckboxCustomFieldAddModal onClose={() => setActiveModal(null)} />
          ) : (
            <CheckboxCustomFieldEditModal
              fieldDef={activeFieldDef as CheckboxCustomFieldDefinitionDto}
              onClose={onModalClose}
            />
          ))}

        {activeModal === FieldType.DROPDOWN &&
          (activeFieldDef === null ? (
            <DropdownCustomFieldAddModal onClose={() => setActiveModal(null)} />
          ) : (
            <DropdownCustomFieldEditModal
              fieldDef={activeFieldDef as DropdownCustomFieldDefinitionDto}
              onClose={onModalClose}
            />
          ))}

        <div className="mx-auto max-w-[700px] space-y-2">
          <Card>
            <CardHeader className="border-b p-4">
              <CardTitle>Dodatkowe pola uczestnika</CardTitle>
              <CardDescription>
                Każdy uczestnik będzie musiał wypełnić te pola podczas
                rejestracji. Możesz dodać różne typy pól, takie jak tekst, lista
                rozwijana czy pole wyboru.
              </CardDescription>
              <CardDescription className="mt-2">
                Campdesk domyślnie zbiera od każdego uczestnika:
              </CardDescription>
              <ul className="list-disc pl-5 text-sm text-neutral-500">
                <li>imię i nazwisko</li>
                <li>data urodzenia</li>
              </ul>
              <CardDescription>
                Dodatkowo osoba zapisująca się musi podać: e-mail, numer
                telefonu, adres, kod pocztowy oraz miasto.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {customFields?.length === 0 ? (
                <NoCustomFieldsPlaceholder openModal={handleNewCustomField} />
              ) : (
                <>
                  <CustomFieldsSummary openEditModal={openEditModal} />

                  <CardContent className="py-2">
                    <ActionButton
                      tone="outline"
                      size="sm"
                      onClick={handleNewCustomField}
                    >
                      <Plus />
                      Dodaj pole dodatkowe
                    </ActionButton>
                  </CardContent>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const NoCustomFieldsPlaceholder = ({
  openModal,
}: {
  openModal: () => void;
}) => {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-b-[13px] bg-neutral-50 p-4">
      <div className="flex flex-col items-center gap-4">
        <div className="flex h-[60px] w-[60px] items-center justify-center rounded-md bg-basic-white">
          <ListPlus size={24} className="text-neutral-400" />
        </div>
        <div className="space-y-1 text-center">
          <p className="font-medium text-base-text">Brak pól dodatkowych</p>
          <p className="text-sm text-neutral-600">
            Dodaj pierwsze pole dodatkowe
          </p>
        </div>
      </div>
      <ActionButton tone="outline" size="sm" onClick={openModal}>
        <Plus />
        Dodaj pole dodatkowe
      </ActionButton>
    </div>
  );
};

export default TourCustomFieldsConfigPage;
